import React from 'react';
import {
  Card,
  Input,
  MultipleSelect,
  Select,
  SpinnerIcon,
} from '@avila-tek/ui/src';
import { Button } from '@avila-tek/ui/src/Button';
import { FormHeader } from '../../shared/Forms';
import {
  DashboardFilters,
  DashboardFiltersActionType,
  ACTIONS,
} from './reducer';
import { Company, Region } from '../../../models';

const relationshipOptions = [
  {
    _id: 'direct',
    name: 'Directa',
  },
  {
    _id: 'indirect',
    name: 'Indirecta',
  },
];

interface FiltersProps {
  state: Partial<DashboardFilters>;
  dispatch: React.Dispatch<DashboardFiltersActionType>;
  company: Company;
  loading: boolean;
  refreshDashboard: () => void;
}

function Filters({
  state,
  dispatch,
  company,
  loading,
  refreshDashboard,
}: FiltersProps) {
  const { companyDetails } = company;

  // Set initial selectedGoal and subRegions state
  const initialGoal = companyDetails?.goals?.[0];
  const [subRegions, setSubRegions] = React.useState(
    state.regions?.length === 1
      ? companyDetails?.regions?.find(
          (region) => region.name === state.regions[0]
        )?.subRegions ?? []
      : []
  );
  // Set initial showDateFilters state
  const [showDateFilters, setShowDateFilters] = React.useState<boolean>(false);

  const buildOptions = (options: any) =>
    options?.map((option) => ({
      ...option,
      _id: option,
      name: option,
    }));

  const buildRegionsOptions = (regions: Region[]) =>
    regions?.map((region) => ({
      ...region,
      _id: region?.name,
      name: region?.name,
    }));

  const handleChangeSelectMultiple = (value: Array<any>, name: string) => {
    dispatch({
      type: name as any,
      payload: value.map((item) => item?._id) as any,
    });
  };

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    dispatch({ type: name as any, payload: value });
    if (name === ACTIONS.GOAL) {
      dispatch({ type: ACTIONS.PERIOD as any, payload: '' });
    }
  };

  const handleShowDateFilters = () => {
    if (showDateFilters) {
      dispatch({ type: ACTIONS.START_DATE as any, payload: '' });
      dispatch({ type: ACTIONS.END_DATE as any, payload: '' });
    }
    setShowDateFilters((prev) => !prev);
  };

  React.useEffect(() => {
    if (state?.regions?.length === 1) {
      const region = companyDetails?.regions?.find(
        (_region) => _region?.name === state?.regions[0]
      );
      setSubRegions(region?.subRegions ?? []);
    } else {
      setSubRegions([]);
      dispatch({ type: ACTIONS.SUB_REGION, payload: '' });
    }
  }, [state?.regions, companyDetails?.regions, dispatch]);

  // Set initial goal to state variable
  React.useEffect(() => {
    dispatch({
      type: 'GOAL' as any,
      payload: initialGoal?._id,
    });
  }, []);

  return (
    <Card className="w-full flex relative">
      {loading ? (
        <div className="w-full h-full flex justify-center items-center opacity-70 absolute z-50 bg-slate-100">
          <SpinnerIcon className="m-auto w-24 h-24 text-gray-200 animate-spin fill-purple-300" />
        </div>
      ) : null}
      <FormHeader title="Filtrado" />
      <Card.Body>
        <div className="flex w-full flex-wrap ">
          <div className="px-2 flex w-full justify-end space-x-2">
            <input
              type="checkbox"
              checked={showDateFilters}
              onChange={handleShowDateFilters}
              className="w-4 h-4 mt-1 cursor-pointer bg-white rounded border-purple-200 focus:ring-purple-300 focus:ring-2"
            />
            <label className="text-nowrap">Período personalizado</label>
          </div>
          <div className="flex flex-col lg:flex-row justify-start items-center w-full mb-5">
            <div className="w-full lg:w-1/4 px-2">
              <Select
                label="Métrica"
                value={state.goal}
                name={ACTIONS.GOAL}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="" disabled>
                  Seleccione
                </option>
                {companyDetails?.goals?.map((goal) => (
                  <option key={goal._id} value={goal._id}>
                    {goal.metric}
                  </option>
                ))}
              </Select>
            </div>
            <div className="w-full lg:w-1/4 px-2">
              <Select
                label="Período"
                value={state.period ?? ''}
                name={ACTIONS.PERIOD}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="" disabled>
                  Seleccione
                </option>
                {state.goal &&
                  companyDetails?.goals
                    ?.find((goal) => goal._id === state.goal)
                    ?.periods?.map((period, index) => (
                      <option key={index} value={period._id}>
                        {period.name ?? 'Período sin nombre'}
                      </option>
                    ))}
              </Select>
            </div>
            {showDateFilters && (
              <>
                <div className="w-full lg:w-1/4 px-2">
                  <Input
                    name={ACTIONS.START_DATE}
                    value={state.startDate ?? ''}
                    label="Fecha de inicio"
                    type="date"
                    onChange={handleChange}
                    required
                    disabled={loading}
                  />
                </div>
                <div className="w-full lg:w-1/4 px-2">
                  <Input
                    name={ACTIONS.END_DATE}
                    value={state.endDate ?? ''}
                    label="Fecha de finalización"
                    type="date"
                    placeholder="Ingrese un valor para la meta"
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>
              </>
            )}
          </div>
          <div className="flex flex-col lg:flex-row justify-start items-center w-full">
            <div className="w-full lg:w-1/3 px-2">
              <MultipleSelect
                actionName={ACTIONS.RELATIONSHIP}
                options={relationshipOptions ?? []}
                label="Vinculación laboral"
                onChangeSelect={handleChangeSelectMultiple}
                description="Seleccione"
                className=""
              />
            </div>
            <div className="w-full lg:w-1/3 px-2">
              <MultipleSelect
                actionName={ACTIONS.ROLES}
                options={buildOptions(companyDetails?.structure) ?? []}
                label="Nivel jerárquico"
                onChangeSelect={handleChangeSelectMultiple}
                description="Seleccione"
                className=""
              />
            </div>
            <div className="w-full lg:w-1/3 px-2">
              <MultipleSelect
                actionName={ACTIONS.SALE_CHANNELS}
                options={buildOptions(companyDetails?.saleChannels) ?? []}
                label="Canal comercial"
                onChangeSelect={handleChangeSelectMultiple}
                description="Seleccione"
                className=""
              />
            </div>
          </div>
        </div>
        <div className="flex w-full flex-wrap">
          <div className="w-full lg:w-1/3 px-2">
            <MultipleSelect
              actionName={ACTIONS.REGIONS}
              defaultSelect={buildOptions(state?.regions) ?? []}
              options={buildRegionsOptions(companyDetails?.regions) ?? []}
              label="Región"
              onChangeSelect={handleChangeSelectMultiple}
              description="Seleccione"
              className=""
            />
          </div>
          <div className="w-full lg:w-1/3 px-2">
            <Select
              label="Subregión"
              value={state?.subRegion ?? ''}
              name={ACTIONS.SUB_REGION}
              onChange={handleChange}
            >
              <option value="" disabled>
                Seleccione
              </option>
              {subRegions?.map((subRegion) => (
                <option key={subRegion} value={subRegion}>
                  {subRegion}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-full lg:w-1/3 px-2 flex items-center">
            <Button
              className="mt-5"
              onClick={(e) => {
                e.preventDefault();
                refreshDashboard();
              }}
            >
              Filtrar
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

export default Filters;
